import isEmpty from 'lodash/isEmpty';
import upperFirst from 'lodash/upperFirst';
import type { JSONSchema, SimpleTypes } from '../schema-types';

type SchemaProperty = {
  key: string;
  type: SimpleTypes;
  isRequired: boolean;
};

type Schema = { key: string; properties: SchemaProperty[] };

export type SchemaForm = { schema: Schema[] };

const getSchemaProperty = (value: JSONSchema): SchemaProperty[] => {
  return Object.entries(value.properties || {}).map(([key, schema]) => ({
    key,
    type: Array.isArray(schema.type) ? schema.type[0] : schema.type,
    isRequired: !!value.required?.includes(key),
  }));
};

export const getSchemaProperties = (schema: Record<string, unknown>): Schema[] => {
  // first filter out the values that are not strings and parse the remaining values as JSON
  const validSchema = Object.fromEntries(
    Object.entries(schema).filter(([_, value]) => typeof value === 'string'),
  ) as Record<string, string>;

  try {
    // JSON.parse can throw an error if the value is not a valid JSON
    const parsedSchema = Object.fromEntries(
      Object.entries(validSchema).map(([key, value]) => [key, JSON.parse(value)]),
    ) as Record<string, JSONSchema>;
    if (isEmpty(parsedSchema)) {
      return [{ key: '', properties: [{ key: '', type: 'string', isRequired: true }] }];
    }
    return Object.entries(parsedSchema).map(([key, value]) => ({ key, properties: getSchemaProperty(value) }));
  } catch (error) {
    console.error(error);
    return [{ key: '', properties: [{ key: '', type: 'string', isRequired: true }] }];
  }
};

export const formToSchema = (formSchema: Schema[]): Record<string, string> => {
  return Object.fromEntries(
    formSchema.map(({ key, properties }) => {
      const schemaValue: JSONSchema = {
        title: `${upperFirst(key)}Input`,
        type: 'object',
        $schema: 'http://json-schema.org/draft-07/schema#',
        properties: Object.fromEntries(properties.map(({ key, type }) => [key, { type }])),
        required: properties.filter(({ isRequired }) => isRequired).map(({ key }) => key),
      };

      return [key, JSON.stringify(schemaValue)];
    }),
  );
};
